import React, { useState, useRef, useCallback } from 'react';
import {
    Grid,
    GridItem,
    VStack,
    HStack,
    Box,
    Text,
    Icon,
    IconButton,
    Tooltip,
    Divider,
    Menu,
    MenuItem,
    MenuList,
    MenuButton,
    MenuDivider,
    StackProps,
} from '@chakra-ui/react';
import { HiOutlineHome, HiOutlineDotsHorizontal } from 'react-icons/hi';
import { GoSidebarExpand, GoPlus } from 'react-icons/go';
import { AiOutlineInbox } from 'react-icons/ai';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { IconType } from 'react-icons';
import { ReportingSidebarViewProps } from './reportingSidebarProps';
import { TreeItemContainerProps, TreeItemViewProps } from '../../view/tree/item';
import type { ReportingShellConfig } from '../../reportingShellConfig';

export function createReportingSidebarView(
    config: ReportingShellConfig & {
        Container: {
            Tree: React.FC<TreeItemContainerProps>;
        };
    }
): React.FC<ReportingSidebarViewProps> {
    const {
        Container: { Tree: TreeItemContainer },
        UI,
    } = config;

    const SidebarItem: React.FC<{
        as?: React.ReactElement<StackProps>;
        label: React.ReactElement;
        Icon?: IconType | null;
        path?: string | null;
        rightContent: React.ReactElement;
    }> = (props) => {
        const { NavLink } = UI.Application;

        return (
            <HStack
                as={props.path ? NavLink : undefined}
                to={props.path ?? undefined}
                w="full"
                justify="space-between"
                p={2}
                cursor="pointer"
                fontWeight="medium"
                userSelect="none"
                borderRadius="md"
                color="whiteAlpha.600"
                _hover={{ bg: 'whiteAlpha.50', textDecoration: 'none' }}
                _activeLink={{
                    bg: 'whiteAlpha.100',
                    color: 'whiteAlpha.800',
                }}
                {...props.as?.props}
            >
                <HStack spacing={2}>
                    {props.Icon && (
                        <IconButton
                            aria-label=""
                            as={props.path ? NavLink : undefined}
                            to={props.path ?? undefined}
                            cursor="default"
                            size="sm"
                            h={6}
                            w={6}
                            minW={0}
                            minH={0}
                            variant="outline"
                            bg="none"
                            border="none"
                            icon={
                                props.Icon ? (
                                    <Icon as={props.Icon} fontSize="lg" />
                                ) : undefined
                            }
                            color="whiteAlpha.600"
                            _hover={{ bg: 'none' }}
                            _activeLink={{
                                color: 'whiteAlpha.800',
                            }}
                        />
                    )}
                    {props.label}
                </HStack>
                {props.rightContent && <HStack>{props.rightContent}</HStack>}
            </HStack>
        );
    };

    function createTreeNode(treeConfig: {
        ident: boolean;
        variant: 'primary' | 'secondary';
    }): React.FC<TreeItemViewProps & { children: React.ReactNode }> {
        const Component: React.FC<TreeItemViewProps & { children: React.ReactNode }> = (
            props
        ) => {
            const IconType = props.isExpanded ? ChevronDownIcon : ChevronRightIcon;
            let textColor = 'whiteAlpha.600';
            if (props.isCurrent) {
                textColor = 'whiteAlpha.800';
            }
            if (treeConfig.variant === 'secondary' && props.isCurrent) {
                textColor = 'blue.300';
            }
            if (
                treeConfig.variant === 'secondary' &&
                !props.isCurrent &&
                props.depth === 0
            ) {
                textColor = 'whiteAlpha.600';
            }
            let content = (
                <HStack
                    {...props.container}
                    aria-selected={props.isCurrent}
                    w="full"
                    pl={props.depth * 3}
                    _hover={{ bg: 'whiteAlpha.50' }}
                    {...(treeConfig.variant === 'primary' && {
                        _selected: {
                            bg: 'whiteAlpha.100',
                            _hover: {
                                bg: 'whiteAlpha.200',
                            },
                        },
                    })}
                    borderRadius="md"
                    role="group"
                >
                    <HStack
                        w="full"
                        cursor={props.link ? 'pointer' : undefined}
                        userSelect="none"
                        spacing={2}
                        p={2}
                    >
                        {props.hasChildren && (
                            <>
                                <IconButton
                                    {...props.expand.button}
                                    flexShrink={0}
                                    variant="outline"
                                    size="sm"
                                    h={6}
                                    w={6}
                                    minW={0}
                                    minH={0}
                                    icon={
                                        <Icon
                                            as={IconType}
                                            fontSize="lg"
                                            color="whiteAlpha.500"
                                        />
                                    }
                                    aria-label="Toggle children"
                                    bg="none"
                                    display="none"
                                    _hover={{ bg: 'whiteAlpha.50' }}
                                    _groupHover={{ display: 'block' }}
                                />
                            </>
                        )}
                        {props.icon && (
                            <IconButton
                                flexShrink={0}
                                variant="outline"
                                size="sm"
                                h={6}
                                w={6}
                                minW={0}
                                minH={0}
                                icon={
                                    <>
                                        {props.icon.kind === 'icon' && (
                                            <Icon
                                                as={props.icon.Component}
                                                fontSize="lg"
                                                color={textColor}
                                            />
                                        )}
                                        {props.icon.kind === 'emoji' && (
                                            <Text fontSize="lg">{props.icon.emoji}</Text>
                                        )}
                                    </>
                                }
                                aria-label="Toggle children"
                                bg="none"
                                display="block"
                                _hover={{ bg: 'none' }}
                                _groupHover={props.hasChildren ? { display: 'none' } : {}}
                            />
                        )}
                        <Text noOfLines={1} color={textColor} whiteSpace="discard-after">
                            {props.label}
                        </Text>
                    </HStack>
                    {props.menu && (
                        <HStack p={2} flexShrink={0}>
                            <Menu
                                autoSelect={false}
                                isOpen={props.menu.isOpen}
                                onClose={props.menu.onClose}
                                onOpen={props.menu.onOpen}
                            >
                                <MenuButton
                                    {...props.menu.trigger.button}
                                    color="whiteAlpha.500"
                                    as={IconButton}
                                    size="sm"
                                    h={6}
                                    w={6}
                                    minW={0}
                                    minH={0}
                                    variant="outline"
                                    bg="none"
                                    icon={
                                        <Icon
                                            fontSize="lg"
                                            as={HiOutlineDotsHorizontal}
                                        />
                                    }
                                    visibility="hidden"
                                    _hover={{ bg: 'whiteAlpha.50' }}
                                    _expanded={{ bg: 'whiteAlpha.100' }}
                                    _groupHover={{ visibility: 'visible' }}
                                />
                                <MenuList minW="16rem" bg="black" p={0} overflow="hidden">
                                    <VStack
                                        align="start"
                                        w="full"
                                        bg="whiteAlpha.200"
                                        p={1}
                                        spacing={0}
                                    >
                                        {props.menu.items.map((item, index) => {
                                            if (item.kind === 'divider') {
                                                return (
                                                    <MenuDivider
                                                        key={index}
                                                        my={1}
                                                        bg="whiteAlpha.100"
                                                        borderColor="whiteAlpha.200"
                                                        w="full"
                                                    />
                                                );
                                            }
                                            return (
                                                <MenuItem
                                                    key={index}
                                                    borderRadius="md"
                                                    py={1}
                                                    onClick={item.onClick ?? undefined}
                                                    fontSize="sm"
                                                    fontWeight="medium"
                                                    color="whiteAlpha.800"
                                                    icon={
                                                        item.Icon ? (
                                                            <Icon
                                                                fontSize="lg"
                                                                as={item.Icon}
                                                            />
                                                        ) : undefined
                                                    }
                                                    _hover={{
                                                        bg: 'whiteAlpha.50',
                                                        color: item.colorScheme
                                                            ? `${item.colorScheme}.400`
                                                            : undefined,
                                                    }}
                                                >
                                                    {item.label}
                                                </MenuItem>
                                            );
                                        })}
                                        <MenuDivider
                                            my={1}
                                            bg="whiteAlpha.100"
                                            borderColor="whiteAlpha.200"
                                            w="full"
                                        />
                                        <MenuItem
                                            py={1}
                                            borderRadius="md"
                                            userSelect="none"
                                            color="whiteAlpha.500"
                                            cursor="default"
                                            _hover={{ bg: 'none' }}
                                            fontSize="xs"
                                        >
                                            <VStack align="start" w="full" spacing={0}>
                                                <Text>Last edited by Susie</Text>
                                                <Text>Apr 17, 2025, 9:30 AM</Text>
                                            </VStack>
                                        </MenuItem>
                                    </VStack>
                                </MenuList>
                            </Menu>
                        </HStack>
                    )}
                </HStack>
            );
            if (props.link) {
                content = (
                    <UI.Application.Link style={{ width: '100%' }} {...props.link}>
                        {content}
                    </UI.Application.Link>
                );
            }
            return (
                <>
                    <VStack w="full" align="start" spacing={0.5}>
                        {content}
                    </VStack>
                    {props.isExpanded && props.hasChildren ? (
                        <VStack w="full" align="start" spacing={0.5}>
                            {props.children}
                        </VStack>
                    ) : null}
                </>
            );
        };
        return Component;
    }

    const FavoriteTreeNode = createTreeNode({
        ident: false,
        variant: 'secondary',
    });

    const WorkspaceTreeNode = createTreeNode({
        ident: true,
        variant: 'primary',
    });

    return (props) => {
        const { width } = props.layout;
        const [isScrolled, setIsScrolled] = useState(false);
        const [isScrolling, setIsScrolling] = useState(false);
        const scrollableRef = useRef<HTMLDivElement>(null);
        const scrollTimeoutRef = useRef<NodeJS.Timeout>();

        const handleScroll = useCallback(() => {
            if (scrollableRef.current) {
                setIsScrolled(scrollableRef.current.scrollTop > 0);
            }

            setIsScrolling(true);

            // Clear existing timeout
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            // Set new timeout to hide scrollbar
            scrollTimeoutRef.current = setTimeout(() => {
                setIsScrolling(false);
            }, 1000); // Hide after 1 second of no scrolling
        }, []);

        React.useEffect(() => {
            return () => {
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }
            };
        }, []);

        return (
            <Grid
                h="full"
                w="full"
                templateColumns="1fr min-content"
                width={width}
                gap={0}
            >
                <GridItem
                    as={Grid}
                    w="full"
                    h="full"
                    gap={0}
                    fontSize="md"
                    fontWeight="medium"
                    templateRows="min-content min-content 1fr"
                >
                    <GridItem p={{ base: 2, lg: 4 }}>
                        <VStack align="start" w="full" spacing={0.5}>
                            {props.navigation.items.map((item) => (
                                <SidebarItem
                                    key={item.id}
                                    label={
                                        <Text letterSpacing="tight">{item.label}</Text>
                                    }
                                    Icon={item.Icon}
                                    path={item.path}
                                    rightContent={<></>}
                                />
                            ))}
                            {/* <SidebarItem
                                label={<Text letterSpacing="tight">Home</Text>}
                                Icon={HiOutlineHome}
                                path="./home"
                                rightContent={
                                    <Tooltip
                                        placement="bottom"
                                        bg="gray.50"
                                        color="gray.900"
                                        py={1}
                                        px={2}
                                        label="Collapse sidebar"
                                        borderRadius="md"
                                        openDelay={500}
                                    >
                                        <IconButton
                                            flexShrink={0}
                                            variant="outline"
                                            size="sm"
                                            h={6}
                                            w={6}
                                            minW={0}
                                            minH={0}
                                            icon={
                                                <Icon
                                                    as={GoSidebarExpand}
                                                    fontSize="lg"
                                                    color="whiteAlpha.500"
                                                />
                                            }
                                            aria-label="Toggle children"
                                            bg="none"
                                            _hover={{ bg: 'whiteAlpha.50' }}
                                        />
                                    </Tooltip>
                                }
                            /> */}
                            {props.favorites && (
                                <VStack
                                    w="full"
                                    align="start"
                                    spacing={0.5}
                                    flexShrink={0}
                                >
                                    {props.favorites.map((node) => (
                                        <TreeItemContainer
                                            key={node.item.id}
                                            as={FavoriteTreeNode}
                                            {...node}
                                        />
                                    ))}
                                </VStack>
                            )}
                        </VStack>
                    </GridItem>
                    <GridItem px={{ base: 2, lg: 4 }}>
                        <Divider w="full" borderWidth={1} borderColor="whiteAlpha.200" />
                    </GridItem>
                    <GridItem position="relative">
                        <Box
                            id="sidebar-shadow"
                            position="absolute"
                            top={0}
                            left={{ base: 2, lg: 4 }}
                            right={{ base: 2, lg: 4 }}
                            h={6}
                            bgGradient="linear(to-b, rgba(0,0,0,0.5), transparent)"
                            zIndex={10}
                            pointerEvents="none"
                            opacity={isScrolled ? 1 : 0}
                            transition="opacity 0.3s ease-in-out"
                        />

                        <VStack
                            ref={scrollableRef}
                            onScroll={handleScroll}
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            align="start"
                            w="full"
                            spacing={4}
                            p={{ base: 2, lg: 4 }}
                            overflowY="scroll"
                            pb={{ base: 32, lg: 36, xl: 40, '2xl': 44 }}
                            sx={{
                                scrollbarGutter: 'stable',
                                '&::-webkit-scrollbar': {
                                    '-webkit-appearance': 'none',
                                    width: '0.5rem',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: 'transparent',
                                },
                                '&::-webkit-scrollbar-corner': {
                                    '-webkit-appearance': 'none',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    borderRadius: '1rem',
                                    background: 'rgba(255,255,255,0.3)',
                                    display: isScrolling ? 'block' : 'none',
                                },
                            }}
                        >
                            {props.folders.length === 0 && (
                                <VStack
                                    p={8}
                                    w="full"
                                    justify="center"
                                    bg="whiteAlpha.50"
                                    borderRadius="md"
                                    color="whiteAlpha.500"
                                >
                                    <Icon fontSize="3xl" as={AiOutlineInbox} />
                                    <Text
                                        textTransform="uppercase"
                                        fontSize="xs"
                                        fontWeight="bold"
                                        letterSpacing="wide"
                                    >
                                        Workspace is empty
                                    </Text>
                                </VStack>
                            )}
                            {props.folders.map((folder) => (
                                <VStack
                                    key={folder.id}
                                    w="full"
                                    align="start"
                                    spacing={0.5}
                                    flexShrink={0}
                                >
                                    <SidebarItem
                                        as={<HStack pl={3} py={1} />}
                                        label={
                                            <Text
                                                color="whiteAlpha.500"
                                                fontSize="xs"
                                                textTransform="uppercase"
                                                fontWeight="bold"
                                                letterSpacing="wide"
                                            >
                                                {folder.label}
                                            </Text>
                                        }
                                        rightContent={
                                            <Tooltip
                                                placement="bottom"
                                                bg="gray.50"
                                                color="gray.900"
                                                py={1}
                                                px={2}
                                                borderRadius="md"
                                                openDelay={500}
                                                label="Add workspace item"
                                            >
                                                <IconButton
                                                    flexShrink={0}
                                                    variant="outline"
                                                    size="sm"
                                                    h={6}
                                                    w={6}
                                                    minW={0}
                                                    minH={0}
                                                    icon={
                                                        <Icon
                                                            as={GoPlus}
                                                            fontSize="xl"
                                                            color="whiteAlpha.500"
                                                        />
                                                    }
                                                    aria-label="Add"
                                                    bg="none"
                                                    _hover={{ bg: 'whiteAlpha.100' }}
                                                />
                                            </Tooltip>
                                        }
                                    />
                                    {folder.items.map((node) => (
                                        <TreeItemContainer
                                            key={node.item.id}
                                            as={WorkspaceTreeNode}
                                            {...node}
                                        />
                                    ))}
                                </VStack>
                            ))}
                        </VStack>
                    </GridItem>
                </GridItem>
                <GridItem zIndex="docked">
                    <Box
                        aria-pressed={props.resize.isResizing}
                        {...props.resize.handle}
                        flexShrink={0}
                        h="full"
                        cursor="col-resize"
                        w={3}
                        position="relative"
                        _before={{
                            content: '""',
                            position: 'absolute',
                            left: '5px',
                            top: 0,
                            bottom: 0,
                            width: '2px',
                            bg: 'whiteAlpha.200',
                            pointerEvents: 'none',
                        }}
                        _hover={{
                            _before: {
                                bg: 'whiteAlpha.300',
                            },
                        }}
                        _pressed={{
                            _before: {
                                bg: 'whiteAlpha.400',
                            },
                        }}
                    />
                </GridItem>
            </Grid>
        );
    };
}
